import React from "react";

import { CalendarClock, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";

import SectionCard from "@/pages/appointmentDetails/SectionCard";

/**
 * Mirrors ``TERMINAL_APPOINTMENT_STATUSES`` in
 * ``backend/app/models/appointments.py``. A Completed or Sold Appointment
 * cannot be rescheduled as the SAME Appointment -- PUT /appointments/{id}
 * answers 409 -- so its row must not offer the action at all. Booking again
 * after a terminal outcome is done with the section-level "+", which creates
 * a NEW Appointment. Keep this set in step with the backend constant.
 */
const TERMINAL_APPOINTMENT_STATUSES = new Set(["Completed", "Sold"]);

function canRescheduleAppointment(appointment) {
  return !TERMINAL_APPOINTMENT_STATUSES.has(String(appointment?.status || "").trim());
}

export default function AppointmentHistorySection({
  accentIconButtonClass,
  onRescheduleAppointment,
  setFollowUpOpen,
  setAppointmentsEditMode,

  appointmentHistory,
  appointmentsEditMode,
  canDeleteAppointment,
  handleDeleteAppointment,
  fmtDateTime,
  setSummaryAppt,
  setSummaryOpen,
}) {
  return (
    <SectionCard
      title="Appointments"
      right={
        <div className="flex items-center gap-2">
          {/* "+" is the ONE creation action: it books a NEW Appointment and
              never reschedules an existing one.

              There is deliberately no Reschedule control up here. A
              section-level Reschedule cannot say WHICH Appointment it would
              move, and once a prospect has several it silently targeted the
              one named in the route rather than the one the user was looking
              at. Rescheduling now lives on each row, bound to that row's id. */}
          <Button
            type="button"
            variant="secondary"
            className={accentIconButtonClass}
            onClick={() => setFollowUpOpen(true)}
            title="New Appointment"
            aria-label="New Appointment"
            data-testid="appointment-details-appointments-add-button"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            className={accentIconButtonClass}
            onClick={() => setAppointmentsEditMode((prev) => !prev)}
            data-testid="appointment-details-appointments-edit-button"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      }
    >
      <div className="space-y-2">
        {appointmentHistory.map((a) => {
          const showReschedule = canRescheduleAppointment(a);
          const showDelete = appointmentsEditMode && canDeleteAppointment(a);
          const controlCount = (showReschedule ? 1 : 0) + (showDelete ? 1 : 0);

          return (
          <div key={a.id} className="relative">
            <button
              type="button"
              className={`w-full rounded-xl bg-white/5 px-4 py-3 pl-3 text-left ring-1 ring-white/10 border-l-4 border-indigo-500/60 hover:bg-white/10 ${
                controlCount === 2 ? "pr-20" : controlCount === 1 ? "pr-12" : ""
              }`}
              onClick={() => {
                setSummaryAppt(a);
                setSummaryOpen(true);
              }}
              data-testid={`appointment-history-item-${a.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="space-y-1">
                  <div className="text-xs font-semibold text-indigo-200" data-testid={`appointment-history-status-${a.id}`}>
                    {String(a.status || "").toUpperCase() || "—"}
                  </div>
                  <div className="text-sm text-slate-100" data-testid={`appointment-history-date-${a.id}`}>
                    {fmtDateTime(a.start_at)}
                  </div>
                </div>
                <div className="min-w-0 flex-1 border-l border-white/10 pl-4">
                  <div className="text-sm text-slate-200/80 line-clamp-2" data-testid={`appointment-history-summary-${a.id}`}>
                    {a.summary_payload?.summary || a.summary_payload?.appointment_outcome || ""}
                  </div>
                </div>
              </div>
            </button>

            {/* Row controls sit OUTSIDE the row button -- a button may not
                nest inside a button -- so a click here cannot bubble into the
                row. stopPropagation is kept anyway so the guarantee survives
                the wrapper ever gaining a click handler of its own. */}
            {controlCount > 0 ? (
              <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
                {showReschedule ? (
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/30 hover:bg-indigo-500/25"
                    onClick={(event) => {
                      event.stopPropagation();
                      // THIS row's Appointment, never the route's.
                      onRescheduleAppointment(a);
                    }}
                    title="Reschedule Appointment"
                    aria-label="Reschedule Appointment"
                    data-testid={`appointment-history-reschedule-${a.id}`}
                  >
                    <CalendarClock className="h-4 w-4" />
                  </button>
                ) : null}

                {showDelete ? (
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-500/15 text-rose-200 ring-1 ring-rose-400/30 hover:bg-rose-500/25"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDeleteAppointment(a);
                    }}
                    data-testid={`appointment-history-delete-${a.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          );
        })}

        {appointmentHistory.length === 0 ? (
          <div className="text-sm text-slate-200/70" data-testid="appointment-history-empty">
            No appointments found.
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}
